/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button, Sidebar, SelectButton, MultipleSelect } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { Fieldset } from "~/components/Forms";
import { ServiceGroupSelect } from "~/components/Services/ServiceAndGroupAssign/ServiceGroupSelect";
import { ServiceSelect } from "~/components/Services/ServiceAndGroupAssign/ServiceSelect";
import { showSuccess } from "~/components/Toast";
import {
  ARAttachedServices,
  AssignmentRuleRouteParams,
} from "~/models/assignmentRule";
import { ACTIONS, Can } from "~/routing";
import { useAxios, generateActionSuccessText } from "~/tools";
import FlexContainer from "../Styled/FlexContainer";

type ARServiceAndGroupAssignProps = {
  refetch: () => void;
  serviceAttachments: ARAttachedServices[];
};
function ARServiceAndGroupAssign({
  refetch,
  serviceAttachments,
}: ARServiceAndGroupAssignProps) {
  const {
    customerId,
    assignmentRuleId,
  } = useParams<AssignmentRuleRouteParams>();

  const [isService, setIsService] = React.useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [selectedId, setSelectedId] = React.useState("");
  const [isSubmittingLoading, setIsSubmittingLoading] = React.useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const onSubmit = async () => {
    setIsSubmittingLoading(true);
    try {
      isService
        ? await Axios().post(
            `/customers/${customerId}/assignmentRules/${assignmentRuleId}/services`,
            {
              serviceId: selectedId,
            }
          )
        : await Axios().post(
            `/customers/${customerId}/assignmentRules/${assignmentRuleId}/serviceGroups`,
            {
              serviceGroupId: selectedId,
            }
          );
      setIsSidebarOpen(false);
      refetch();
      showSuccess(
        generateActionSuccessText(
          `${isService ? "Services" : "Service Group"}`
        )()("assigned")()
      );
    } catch (e) {
      showError(e.response);
    } finally {
      setIsSubmittingLoading(false);
      setSelectedId("");
    }
  };
  const { handleSubmit } = useForm();

  return (
    <>
      <Can action={ACTIONS.ADD_ASSIGNMENT_SERVICE}>
        <Button
          onClick={() => setIsSidebarOpen(true)}
          css={{ marginLeft: "auto", marginBottom: "1rem" }}
          icon="link"
        >
          Assign
        </Button>
      </Can>
      <Sidebar
        width="30rem"
        title="Assign Service or Service Group"
        isOpen={isSidebarOpen}
        onClick={() => setIsSidebarOpen(false)}
      >
        <Fieldset>
          <MultipleSelect size="small">
            <SelectButton
              name="button1"
              value="service"
              labelText="Service"
              isActive={isService}
              onClick={() => {
                setIsService(true);
                setSelectedId("");
              }}
            />
            <SelectButton
              name="button2"
              value="serviceGroup"
              labelText="Service Group"
              isActive={!isService}
              onClick={() => {
                setIsService(false);
                setSelectedId("");
              }}
            />
          </MultipleSelect>
        </Fieldset>
        {isService ? (
          <ServiceSelect
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            serviceAttachments={serviceAttachments as ARAttachedServices[]}
          />
        ) : (
          <ServiceGroupSelect
            selectedId={selectedId}
            setSelectedId={setSelectedId}
            serviceAttachments={serviceAttachments}
          />
        )}
        <FlexContainer>
          <Button
            aria-label="assign"
            initialState={isSubmittingLoading ? "loading" : ""}
            onClick={handleSubmit(onSubmit)}
            disabled={!selectedId}
          >
            Assign
          </Button>
          <Button
            severity="medium"
            type="button"
            css={{ marginLeft: "auto" }}
            onClick={() => {
              setIsSidebarOpen(false);
            }}
          >
            Cancel
          </Button>
        </FlexContainer>
      </Sidebar>
    </>
  );
}

export { ARServiceAndGroupAssign };
